/**
 * ErvaPOS - Settings Page (Expanded)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Settings,
  Building,
  CreditCard,
  Gift,
  Clock,
  Save,
  Loader2,
  Plus,
  Eye,
  EyeOff,
  Upload,
  Printer,
  Image as ImageIcon,
  Trash2,
  Lock as LockIcon,
} from 'lucide-react';
import { settingsAPI, authAPI } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import UsersManagementTab from './settings/UsersManagementTab';

export const SettingsPage = () => {
  const { isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_new_password: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPassField, setShowPassField] = useState({ current: false, new: false, confirm: false });

  const fileInputRef = useRef(null);
  
  // Form states
  const [companyForm, setCompanyForm] = useState({
    company_name: '',
    company_nif: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    company_capital: '',
    company_logo_url: '',
  });

  const [receiptForm, setReceiptForm] = useState({
    receipt_width: '80',
    receipt_footer_message: 'Obrigado pela sua preferência!',
  });

  const [pointsForm, setPointsForm] = useState({
    loyalty_points_active: true,
    points_per_euro: 1,
    points_value: 0.01,
  });

  const [workForm, setWorkForm] = useState({
    work_start_time: '09:00',
    work_end_time: '18:00',
  });

  const [vendusForm, setVendusForm] = useState({
    location_name: '',
    api_key: '',
    is_active: false,
  });

  const [ifthenpayForm, setIfthenpayForm] = useState({
    mb_key: '',
    mbway_key: '',
    entity: '',
    subentity: '',
    anti_phishing_key: '',
    is_active: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.get();
      const data = response.data;
      setSettings(data);
      
      setCompanyForm({
        company_name: data.company_name || '',
        company_nif: data.company_nif || '',
        company_address: data.company_address || '',
        company_phone: data.company_phone || '',
        company_email: data.company_email || '',
        company_website: data.company_website || '',
        company_capital: data.company_capital || '',
        company_logo_url: data.company_logo_url || '',
      });

      setReceiptForm({
        receipt_width: data.receipt_width || '80',
        receipt_footer_message: data.receipt_footer_message || 'Obrigado pela sua preferência!',
      });

      setPointsForm({
        loyalty_points_active: data.loyalty_points_active !== false,
        points_per_euro: data.points_per_euro ?? 1,
        points_value: data.points_value ?? 0.01,
      });

      setWorkForm({
        work_start_time: data.work_start_time || '09:00',
        work_end_time: data.work_end_time || '18:00',
      });

      if (data.ifthenpay) {
        setIfthenpayForm({
          mb_key: data.ifthenpay.mb_key || '',
          mbway_key: data.ifthenpay.mbway_key || '',
          entity: data.ifthenpay.entity || '',
          subentity: data.ifthenpay.subentity || '',
          anti_phishing_key: data.ifthenpay.anti_phishing_key || '',
          is_active: data.ifthenpay.is_active || false,
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (section, data) => {
    setSaving(true);
    try {
      await settingsAPI.update(data);
      toast.success('Configurações guardadas');
      loadSettings();
    } catch (error) {
      toast.error('Erro ao guardar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Tipo de ficheiro inválido. Use PNG, JPEG ou WebP');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ficheiro muito grande. Máximo 2MB');
      return;
    }

    setSaving(true);
    try {
      const response = await settingsAPI.uploadLogo(file);
      setCompanyForm(prev => ({ ...prev, company_logo_url: response.data.logo_url }));
      toast.success('Logo carregado com sucesso');
    } catch (error) {
      toast.error('Erro ao carregar logo');
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({ company_logo_url: '' });
      setCompanyForm(prev => ({ ...prev, company_logo_url: '' }));
      toast.success('Logo removido');
    } catch (error) {
      toast.error('Erro ao remover logo');
    } finally {
      setSaving(false);
    }
  };

  const addVendusLocation = async () => {
    if (!vendusForm.location_name) {
      toast.error('Nome da localização é obrigatório');
      return;
    }
    setSaving(true);
    try {
      await settingsAPI.addVendusLocation(vendusForm);
      toast.success('Localização Vendus adicionada');
      setVendusForm({ location_name: '', api_key: '', is_active: false });
      loadSettings();
    } catch (error) {
      toast.error('Erro ao adicionar localização');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowKey = (key) => {
    setShowApiKey(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isAdmin && !isManager) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-slate-500">
        <Settings className="w-12 h-12 mb-4" />
        <p>Apenas administradores ou gestores podem aceder às configurações</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white font-['Manrope']">Configurações</h1>
        <p className="text-slate-400">Gestão do sistema, empresa e integrações</p>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="bg-slate-800 border-slate-700 flex-wrap">
          <TabsTrigger value="users" className="data-[state=active]:bg-emerald-500">
              <LockIcon className="w-4 h-4 mr-2" />
              Utilizadores
            </TabsTrigger>
<TabsTrigger value="company" className="data-[state=active]:bg-emerald-500">
            <Building className="w-4 h-4 mr-2" />
            Empresa
          </TabsTrigger>
          <TabsTrigger value="receipts" className="data-[state=active]:bg-emerald-500">
            <Printer className="w-4 h-4 mr-2" />
            Talões
          </TabsTrigger>
          <TabsTrigger value="vendus" className="data-[state=active]:bg-emerald-500">
            <CreditCard className="w-4 h-4 mr-2" />
            Vendus
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-emerald-500">
            <CreditCard className="w-4 h-4 mr-2" />
            Pagamentos
          </TabsTrigger>
          <TabsTrigger value="loyalty" className="data-[state=active]:bg-emerald-500">
            <Gift className="w-4 h-4 mr-2" />
            Fidelização
          </TabsTrigger>
          <TabsTrigger value="schedule" className="data-[state=active]:bg-emerald-500">
            <Clock className="w-4 h-4 mr-2" />
            Horário
          </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-emerald-500">
              <LockIcon className="w-4 h-4 mr-2" />
              Segurança
            </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        
        {/* Users Management */}
        <TabsContent value="users">
          <UsersManagementTab />
        </TabsContent>

<TabsContent value="company">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building className="w-5 h-5 text-emerald-400" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>Informações que aparecem nos documentos e talões</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="space-y-3">
                <Label className="text-slate-300">Logo da Empresa</Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden">
                    {companyForm.company_logo_url ? (
                      <img 
                        src={companyForm.company_logo_url} 
                        alt="Logo" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="w-10 h-10 text-slate-600" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-slate-700"
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Carregar Logo
                    </Button>
                    {companyForm.company_logo_url && (
                      <Button
                        variant="outline"
                        onClick={removeLogo}
                        className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remover
                      </Button>
                    )}
                    <p className="text-xs text-slate-500">PNG, JPEG ou WebP. Máx 2MB</p>
                  </div>
                </div>
              </div>

              <Separator className="bg-slate-700" />

              {/* Company Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nome da Empresa</Label>
                  <Input
                    value={companyForm.company_name}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, company_name: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    placeholder="Ex: Ervanária Saúde Natural"
                    data-testid="company-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">NIF</Label>
                  <Input
                    value={companyForm.company_nif}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, company_nif: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    placeholder="Ex: 123456789"
                    data-testid="company-nif"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Morada</Label>
                <Input
                  value={companyForm.company_address}
                  onChange={(e) => setCompanyForm(prev => ({ ...prev, company_address: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  placeholder="Ex: Rua da Saúde, 123, 1000-001 Lisboa"
                  data-testid="company-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Telefone</Label>
                  <Input
                    value={companyForm.company_phone}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, company_phone: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    placeholder="Ex: +351 210 000 000"
                    data-testid="company-phone"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={companyForm.company_email}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, company_email: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    placeholder="Ex: geral@ervanaria.pt"
                    data-testid="company-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Website</Label>
                  <Input
                    value={companyForm.company_website}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, company_website: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    placeholder="Ex: www.ervanaria.pt"
                    data-testid="company-website"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Capital Social</Label>
                  <Input
                    value={companyForm.company_capital}
                    onChange={(e) => setCompanyForm(prev => ({ ...prev, company_capital: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    placeholder="Ex: 5.000,00 €"
                    data-testid="company-capital"
                  />
                </div>
              </div>

              <Button
                onClick={() => saveSettings('company', companyForm)}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-company"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipt Settings */}
        <TabsContent value="receipts">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Printer className="w-5 h-5 text-emerald-400" />
                Configurações de Talões
              </CardTitle>
              <CardDescription>Formato e conteúdo dos talões de venda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-slate-300">Largura do Talão POS</Label>
                <Select
                  value={receiptForm.receipt_width}
                  onValueChange={(val) => setReceiptForm(prev => ({ ...prev, receipt_width: val }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 w-48" data-testid="receipt-width">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="80">80mm (Padrão)</SelectItem>
                    <SelectItem value="58">58mm (Compacto)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  A maioria das impressoras POS usa 80mm. Impressoras portáteis usam 58mm.
                </p>
              </div>

              <div>
                <Label className="text-slate-300">Mensagem de Rodapé</Label>
                <Textarea
                  value={receiptForm.receipt_footer_message}
                  onChange={(e) => setReceiptForm(prev => ({ ...prev, receipt_footer_message: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  placeholder="Ex: Obrigado pela sua preferência!"
                  rows={2}
                  data-testid="receipt-footer"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Esta mensagem aparece no final de cada talão impresso.
                </p>
              </div>

              <div className="p-4 bg-slate-800 rounded-lg">
                <h4 className="text-sm font-medium text-white mb-2">Numeração de Documentos</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <div className="flex justify-between">
                    <span>NCP (Nota Encomenda Paga):</span>
                    <span className="text-white font-mono">NCP{new Date().getFullYear()}/{String(settings?.ncp_sequence || 0).padStart(4, '0')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Encomendas:</span>
                    <span className="text-white font-mono">ENC{new Date().getFullYear()}/{String(settings?.order_sequence || 0).padStart(4, '0')}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    A numeração fiscal (faturas) será gerida pela API do Vendus quando ativada.
                  </p>
                </div>
              </div>

              <Button
                onClick={() => saveSettings('receipts', receiptForm)}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-receipts"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendus Settings */}
        <TabsContent value="vendus">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Cegid Vendus - Faturação
              </CardTitle>
              <CardDescription>Configure as API Keys para cada localização de faturação</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Existing locations */}
              {settings?.vendus_locations?.map((loc, idx) => (
                <div key={loc.id || idx} className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">{loc.location_name}</h4>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={loc.is_active}
                        onCheckedChange={async (val) => {
                          try {
                            await settingsAPI.update({ vendus_locations: settings.vendus_locations.map((l, i) => i === idx ? { ...l, is_active: val } : { ...l, is_active: false }) });
                            toast.success(val ? "Localização activada" : "Localização desactivada");
                            const res = await settingsAPI.get(); setSettings(res.data);
                          } catch { toast.error("Erro ao actualizar"); }
                        }}
                      />
                      <span className={loc.is_active ? "text-emerald-400 text-sm" : "text-slate-500 text-sm"}>{loc.is_active ? "Ativo" : "Inativo"}</span>
                      <button onClick={async () => {
                        if (!window.confirm("Remover esta localização Vendus?")) return;
                        try {
                          await settingsAPI.deleteVendusLocation(loc.id);
                          toast.success("Localização removida");
                          const res = await settingsAPI.get(); setSettings(res.data);
                        } catch { toast.error("Erro ao remover"); }
                      }} className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-900/20 transition-colors" title="Remover">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">API Key: {loc.api_key ? "••••••••" + loc.api_key.slice(-4) : "Não configurada"}</p>
                  {loc.register_id && <p className="text-xs text-emerald-600 mt-0.5">Register ID: {loc.register_id} ✓</p>}
                  {!loc.register_id && <p className="text-xs text-yellow-600 mt-0.5">⚠ Register ID em falta — remove e adiciona novamente</p>}
                </div>
              ))}

              {(!settings?.vendus_locations || settings.vendus_locations.length === 0) && (
                <div className="p-4 bg-slate-800 rounded-lg text-center text-slate-500">
                  <p>Nenhuma localização Vendus configurada</p>
                </div>
              )}

              <Separator className="bg-slate-700" />

              {/* Add new location */}
              <div className="space-y-4">
                <h4 className="font-medium text-white">Adicionar Nova Localização</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nome da Localização</Label>
                    <Input
                      placeholder="Ex: Loja Lisboa"
                      value={vendusForm.location_name}
                      onChange={(e) => setVendusForm(prev => ({ ...prev, location_name: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                      data-testid="vendus-location-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">API Key Vendus</Label>
                    <div className="relative">
                      <Input
                        type={showApiKey.vendus ? 'text' : 'password'}
                        placeholder="Inserir API Key"
                        value={vendusForm.api_key}
                        onChange={(e) => setVendusForm(prev => ({ ...prev, api_key: e.target.value }))}
                        className="bg-slate-800 border-slate-700 pr-10"
                        data-testid="vendus-api-key"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('vendus')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                      >
                        {showApiKey.vendus ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={vendusForm.is_active}
                    onCheckedChange={(val) => setVendusForm(prev => ({ ...prev, is_active: val }))}
                  />
                  <Label className="text-slate-300">Ativar integração</Label>
                </div>
                <Button
                  onClick={addVendusLocation}
                  disabled={saving}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="add-vendus-location"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Adicionar Localização
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Settings */}
        <TabsContent value="payments">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Ifthenpay - Pagamentos
              </CardTitle>
              <CardDescription>Configure MBWay, Multibanco e outros métodos de pagamento</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">MB Key (Multibanco)</Label>
                  <div className="relative">
                    <Input
                      type={showApiKey.mb ? 'text' : 'password'}
                      value={ifthenpayForm.mb_key}
                      onChange={(e) => setIfthenpayForm(prev => ({ ...prev, mb_key: e.target.value }))}
                      className="bg-slate-800 border-slate-700 pr-10"
                      data-testid="ifthenpay-mb-key"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowKey('mb')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                    >
                      {showApiKey.mb ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">MBWay Key</Label>
                  <div className="relative">
                    <Input
                      type={showApiKey.mbway ? 'text' : 'password'}
                      value={ifthenpayForm.mbway_key}
                      onChange={(e) => setIfthenpayForm(prev => ({ ...prev, mbway_key: e.target.value }))}
                      className="bg-slate-800 border-slate-700 pr-10"
                      data-testid="ifthenpay-mbway-key"
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowKey('mbway')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                    >
                      {showApiKey.mbway ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Entidade</Label>
                  <Input
                    value={ifthenpayForm.entity}
                    onChange={(e) => setIfthenpayForm(prev => ({ ...prev, entity: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    data-testid="ifthenpay-entity"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Subentidade</Label>
                  <Input
                    value={ifthenpayForm.subentity}
                    onChange={(e) => setIfthenpayForm(prev => ({ ...prev, subentity: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    data-testid="ifthenpay-subentity"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Chave Anti-Phishing</Label>
                <div className="relative">
                  <Input
                    type={showApiKey.antiphishing ? 'text' : 'password'}
                    value={ifthenpayForm.anti_phishing_key}
                    onChange={(e) => setIfthenpayForm(prev => ({ ...prev, anti_phishing_key: e.target.value }))}
                    className="bg-slate-800 border-slate-700 pr-10"
                    data-testid="ifthenpay-antiphishing"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowKey('antiphishing')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                  >
                    {showApiKey.antiphishing ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={ifthenpayForm.is_active}
                  onCheckedChange={(val) => setIfthenpayForm(prev => ({ ...prev, is_active: val }))}
                />
                <Label className="text-slate-300">Ativar pagamentos Ifthenpay</Label>
              </div>
              <Button
                onClick={() => saveSettings('ifthenpay', { ifthenpay: ifthenpayForm })}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-ifthenpay"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loyalty Settings */}
        <TabsContent value="loyalty">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-400" />
                Sistema de Fidelização
              </CardTitle>
              <CardDescription>Configure pontos e vouchers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-700 bg-slate-800 p-4">
                <div>
                  <p className="font-semibold text-white">Atribuição automática de pontos</p>
                  <p className="text-sm text-slate-400">Quando inativa, não atribui novos pontos; os saldos existentes continuam disponíveis.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-semibold ${pointsForm.loyalty_points_active ? 'text-emerald-400' : 'text-slate-400'}`}>
                    {pointsForm.loyalty_points_active ? 'Ativa' : 'Inativa'}
                  </span>
                  <Switch
                    checked={pointsForm.loyalty_points_active}
                    onCheckedChange={(checked) => setPointsForm(prev => ({ ...prev, loyalty_points_active: checked }))}
                    data-testid="loyalty-points-active"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Pontos por Euro Gasto</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={pointsForm.points_per_euro}
                    onChange={(e) => setPointsForm(prev => ({ ...prev, points_per_euro: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                    className="bg-slate-800 border-slate-700"
                    data-testid="points-per-euro"
                  />
                  <p className="text-xs text-slate-500 mt-1">Ex: 1 ponto por cada euro gasto</p>
                </div>
                <div>
                  <Label className="text-slate-300">Valor do Ponto (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pointsForm.points_value}
                    onChange={(e) => setPointsForm(prev => ({ ...prev, points_value: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                    className="bg-slate-800 border-slate-700"
                    data-testid="points-value"
                  />
                  <p className="text-xs text-slate-500 mt-1">Ex: 0.01€ = 100 pontos = 1€</p>
                </div>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-400">
                  Com esta configuração: <br />
                  <span className="text-white">
                    {pointsForm.loyalty_points_active
                      ? <>Por cada €{(1).toFixed(2)} gasto elegível, o cliente ganha {pointsForm.points_per_euro} ponto(s). <br /></>
                      : <>A atribuição de novos pontos está suspensa. <br /></>}
                    {Math.ceil(1 / pointsForm.points_value)} pontos = €{(Math.ceil(1 / pointsForm.points_value) * pointsForm.points_value).toFixed(2)} de desconto
                  </span>
                </p>
              </div>
              <Button
                onClick={() => saveSettings('loyalty', pointsForm)}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-loyalty"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Settings */}
        <TabsContent value="schedule">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-400" />
                Horário de Trabalho
              </CardTitle>
              <CardDescription>Horário padrão da empresa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Hora de Entrada</Label>
                  <Input
                    type="time"
                    value={workForm.work_start_time}
                    onChange={(e) => setWorkForm(prev => ({ ...prev, work_start_time: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    data-testid="work-start-time"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Hora de Saída</Label>
                  <Input
                    type="time"
                    value={workForm.work_end_time}
                    onChange={(e) => setWorkForm(prev => ({ ...prev, work_end_time: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                    data-testid="work-end-time"
                  />
                </div>
              </div>
              <Button
                onClick={() => saveSettings('schedule', workForm)}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-schedule"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security">
            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <LockIcon className="w-5 h-5 text-emerald-400" />
              Segurança
                </CardTitle>
                <CardDescription>Alterar palavra-passe da conta atual</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-slate-300">Palavra-passe atual</Label>
                    <div className="relative">
                      <Input
                        type={showPassField.current ? "text" : "password"}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                        className="bg-slate-800 border-slate-700 pr-10"
                        placeholder="••••••••"
                      />
                      <Button type="button" variant="ghost" size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        onClick={() => setShowPassField(p => ({ ...p, current: !p.current }))}>
                        {showPassField.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300">Nova palavra-passe</Label>
                    <div className="relative">
                      <Input
                        type={showPassField.new ? "text" : "password"}
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                        className="bg-slate-800 border-slate-700 pr-10"
                        placeholder="Ex: UmaPasswordNova#2026!"
                      />
                      <Button type="button" variant="ghost" size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        onClick={() => setShowPassField(p => ({ ...p, new: !p.new }))}>
                        {showPassField.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300">Confirmar nova palavra-passe</Label>
                    <div className="relative">
                      <Input
                        type={showPassField.confirm ? "text" : "password"}
                        value={passwordForm.confirm_new_password}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_new_password: e.target.value }))}
                        className="bg-slate-800 border-slate-700 pr-10"
                        placeholder="Repete a nova palavra-passe"
                      />
                      <Button type="button" variant="ghost" size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                        onClick={() => setShowPassField(p => ({ ...p, confirm: !p.confirm }))}>
                        {showPassField.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={async () => {
                      if (!passwordForm.current_password || !passwordForm.new_password) { toast.error("Preenche a palavra-passe atual e a nova palavra-passe"); return; }
                      if (passwordForm.new_password !== passwordForm.confirm_new_password) { toast.error("A confirmação não coincide com a nova palavra-passe"); return; }
                      setSavingPassword(true);
                      try {
                        await authAPI.changePassword(passwordForm.current_password, passwordForm.new_password);
                        toast.success("Palavra-passe alterada com sucesso");
                        setPasswordForm({ current_password: "", new_password: "", confirm_new_password: "" });
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Erro ao alterar palavra-passe");
                      } finally {
                        setSavingPassword(false);
                      }
                    }}
                    disabled={savingPassword}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Alterar palavra-passe
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
